import type { ReactElement } from 'react';
import type { MCPServerRisk } from '@kirin/api-client/contracts/mcp';
import type { SkillRiskUnavailableReason } from '@kirin/api-client/contracts/skills';
import { McpRiskBadge } from '@/components/shared/mcp/McpRiskBadge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SKILL_RISK_UNAVAILABLE_COPY } from './skillRiskCopy';

interface SkillRiskCellProps {
  /**
   * The row's AgentMesh risk (KIR-5099). Undefined from a backend that
   * predates it; null when there is no verdict, with the reason.
   */
  risk: MCPServerRisk | null | undefined;
  reason?: SkillRiskUnavailableReason | null;
}

/**
 * Risk column for Inventory → Skills: AgentMesh's verdict in the MCP Servers
 * badge (both share AgentMesh's five levels), or a dash that says why there is
 * none. "Coming soon" covers a backend that predates the column (deploy skew).
 */
export function SkillRiskCell({
  risk,
  reason,
}: SkillRiskCellProps): ReactElement {
  if (risk === undefined) {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full bg-muted-foreground/50"
        />
        Coming soon
      </span>
    );
  }

  if (risk === null) {
    const copy = SKILL_RISK_UNAVAILABLE_COPY[reason ?? 'not_reported'];
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label={copy}
            className="text-muted-foreground cursor-default"
            tabIndex={0}
          >
            —
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>{copy}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return <McpRiskBadge level={risk.level} />;
}

SkillRiskCell.displayName = 'SkillRiskCell';
